import {
  Alert,
  Box,
  Link,
  Paper,
  Stack,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  TextField,
  Button
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { store } from '../../../config/firebase';
import { useColorMode } from '../../../hooks/colorModeProvider';
import { GameStatus } from '../../../constants/gameConstants';
import { liveGameDocs } from '../../../helpers/games';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';

// Status indicator helper
const getGameStatus = (game) => {
  if (game.status === GameStatus.NOT_STARTED) {
    return { label: 'Not Started', color: 'grey.500' };
  }

  if (game.status === GameStatus.IN_PROGRESS) {
    return { label: game.live ? 'Live' : 'In Progress', color: 'info.main' };
  }

  if (game.status === GameStatus.PLAYOFF) {
    return { label: 'Playoff', color: 'warning.main' };
  }

  if (game.status === GameStatus.COMPLETE) {
    return { label: 'Complete', color: 'success.main' };
  }

  return { label: 'Unknown', color: 'grey.400' };
};

const getTeamLabel = (team) => {
  if (!team) {
    return '-';
  }

  if (team.name) {
    return team.name;
  }

  if (Array.isArray(team.players) && team.players.length > 0) {
    return team.players.join(', ');
  }

  return '-';
};

const getTeamColor = (team, fallback) => {
  const color = team?.color;
  if (!color) {
    return fallback;
  }

  const isHexColor = /^#([0-9A-F]{3}|[0-9A-F]{6})$/i.test(color);
  return isHexColor ? color : `${color}.main`;
};

const OverlayIndex = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { mode, setMode } = useColorMode();
  const [eventName, setEventName] = useState('');
  const [eventSections, setEventSections] = useState([]);
  const [games, setGames] = useState([]);
  const [gameNameFilter, setGameNameFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const isAppView = location.pathname.startsWith('/events/');
  const publicOverlaysLink = `${window.location.origin}/overlay-index/${eventId}`;

  useEffect(() => {
    if (!isAppView) {
      setMode('light');
    }
  }, [isAppView, mode, setMode]);

  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError('');

        const eventRef = doc(store, 'events', eventId);
        const gamesRef = collection(store, 'games');
        const gamesQuery = query(gamesRef, where('event', '==', eventId));

        const [eventSnap, gamesSnap] = await Promise.all([getDoc(eventRef), getDocs(gamesQuery)]);

        if (!isMounted) {
          return;
        }

        const eventData = eventSnap.exists() ? eventSnap.data() : null;
        const eventGames = liveGameDocs(gamesSnap).map((gameDoc) => ({
          id: gameDoc.id,
          ...gameDoc.data()
        }));

        setEventName(eventData?.name || 'Event');
        setEventSections(Array.isArray(eventData?.sections) ? eventData.sections : []);
        setGames(eventGames);
      } catch (err) {
        if (!isMounted) {
          return;
        }

        setError('Unable to load overlay links for this event.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [eventId]);

  const sortedGames = useMemo(() => {
    return [...games].sort((a, b) => {
      const greenCompare = String(a.green || '').localeCompare(String(b.green || ''), undefined, {
        numeric: true
      });
      if (greenCompare !== 0) {
        return greenCompare;
      }

      const rinkCompare = Number(a.rink || 0) - Number(b.rink || 0);
      if (rinkCompare !== 0) {
        return rinkCompare;
      }

      if ((a.round || 0) !== (b.round || 0)) {
        return (a.round || 0) - (b.round || 0);
      }

      const sectionCompare = (a.section || '').localeCompare(b.section || '');
      if (sectionCompare !== 0) {
        return sectionCompare;
      }

      return (a.name || '').localeCompare(b.name || '');
    });
  }, [games]);

  const filteredGames = useMemo(() => {
    const filter = gameNameFilter.trim().toLowerCase();

    if (!filter) {
      return sortedGames;
    }

    return sortedGames.filter((game) => {
      const teamAName = getTeamLabel(game?.teams?.teamA);
      const teamBName = getTeamLabel(game?.teams?.teamB);
      const searchText = [game.name, teamAName, teamBName].join(' ').toLowerCase();
      return searchText.includes(filter);
    });
  }, [sortedGames, gameNameFilter]);

  const buildLiveLink = (gameId) => {
    return `${window.location.origin}/embed/live-game/${gameId}`;
  };

  const buildScorecardOverlayLink = (gameId) => {
    return `${window.location.origin}/embed/scorecard/${gameId}`;
  };

  const buildEventLiveLink = () => {
    return `${window.location.origin}/embed/live/${eventId}`;
  };

  const buildEventHorizontalLiveLink = () => {
    return `${window.location.origin}/embed/horizontallive/${eventId}`;
  };

  const buildEventLiveScorecardLink = () => {
    return `${window.location.origin}/embed/scorecard-live/${eventId}`;
  };

  const buildLogOverlayLink = () => {
    return `${window.location.origin}/embed/ticker/${eventId}`;
  };

  const buildTvLogsOverlayLink = () => {
    return `${window.location.origin}/embed/tvlogs/${eventId}`;
  };

  const buildSectionScorecardsOverlayLink = (sectionId, sectionName) => {
    const sectionNameParam = encodeURIComponent(sectionName || 'Section');
    return `${window.location.origin}/embed/section-scorecards/${eventId}/${sectionId}?sectionName=${sectionNameParam}`;
  };

  const cardSx = {
    p: 1.5,
    border: '2px solid',
    borderColor: 'grey.300',
    boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (error) {
      console.error('Unable to copy link', error);
    }
  };

  const sharePublicView = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${eventName || 'Event'} Overlays`,
          url: publicOverlaysLink
        });
        return;
      }
      await copyToClipboard(publicOverlaysLink);
    } catch (error) {
      console.error('Unable to share link', error);
    }
  };

  const LinkRow = ({ label, href }) => (
    <Stack spacing={0.25}>
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        {label}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Link
          href={href}
          target="_blank"
          rel="noreferrer"
          sx={{
            fontSize: 13,
            overflowWrap: 'anywhere'
          }}>
          {href}
        </Link>
        <Tooltip title="Copy link">
          <IconButton size="small" onClick={() => copyToClipboard(href)}>
            <ContentCopyIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      </Box>
    </Stack>
  );

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Typography variant="h5" fontWeight={700}>
            Overlay Index
          </Typography>
          <Stack direction="row" spacing={1}>
            {isAppView ? (
              <Button
                variant="outlined"
                size="small"
                onClick={() => navigate(`/events/${eventId}`)}>
                Back to Event
              </Button>
            ) : null}
            <Button
              variant="outlined"
              size="small"
              startIcon={<ShareIcon />}
              onClick={sharePublicView}>
              Share Public View
            </Button>
          </Stack>
        </Stack>

        {loading && <Typography>Loading overlays...</Typography>}

        {!loading && error && <Alert severity="error">{error}</Alert>}

        {!loading && !error && sortedGames.length === 0 && (
          <Alert severity="info">No games found for this event.</Alert>
        )}

        {!loading && !error && (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 2fr) minmax(280px, 1fr)' },
              gap: 2,
              alignItems: 'start'
            }}>
            <Stack spacing={1.5}>
              <Typography variant="h6" fontWeight={700}>
                Live Overlays
              </Typography>

              <Paper sx={{ ...cardSx, borderColor: 'info.light' }}>
                <Stack spacing={0.5}>
                  <Typography fontWeight={600}>Current Live Game</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Displays the compact live scorebox in the top-left broadcast corner. This
                    updates automatically as the broadcast rink game changes. For other games, see
                    below.
                  </Typography>
                  <LinkRow label="Current Live Game Overlay" href={buildEventLiveLink()} />
                  <LinkRow
                    label="Current Live Game Horizontal Overlay"
                    href={buildEventHorizontalLiveLink()}
                  />
                  <LinkRow
                    label="Current Live Game Scorecard Overlay"
                    href={buildEventLiveScorecardLink()}
                  />
                </Stack>
              </Paper>

              {sortedGames.length > 0 && (
                <Stack spacing={1}>
                  <Typography variant="h6" fontWeight={700}>
                    Games
                  </Typography>
                  <TextField
                    size="small"
                    placeholder="Filter games by game/team name"
                    value={gameNameFilter}
                    onChange={(event) => setGameNameFilter(event.target.value)}
                    sx={{ maxWidth: 360 }}
                  />
                </Stack>
              )}

              {sortedGames.length > 0 && filteredGames.length === 0 && (
                <Alert severity="info">No games match that name filter.</Alert>
              )}

              {filteredGames.map((game) => {
                const liveOverlayLink = buildLiveLink(game.id);
                const scorecardOverlayLink = buildScorecardOverlayLink(game.id);
                const status = getGameStatus(game);
                const teamALabel = getTeamLabel(game?.teams?.teamA);
                const teamBLabel = getTeamLabel(game?.teams?.teamB);
                return (
                  <Paper key={game.id} sx={{ ...cardSx, borderColor: 'primary.light' }}>
                    <Stack
                      spacing={0.5}
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between">
                      <Box>
                        <Typography fontWeight={600}>{game.name || game.id}</Typography>
                        <Box display="flex" alignItems="center" gap={0.75}>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{ color: getTeamColor(game?.teams?.teamA, 'success.main') }}>
                            {teamALabel}
                          </Typography>
                          <Typography variant="body1" color="text.primary" fontWeight={700}>
                            vs
                          </Typography>
                          <Typography
                            variant="body1"
                            fontWeight={700}
                            sx={{ color: getTeamColor(game?.teams?.teamB, 'error.main') }}>
                            {teamBLabel}
                          </Typography>
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {game.green || '-'} {game.rink ? `Rink ${game.rink}` : ''} | Round{' '}
                          {game.round || '-'}
                        </Typography>
                        <Stack spacing={0.25}>
                          <LinkRow label="Live Game Overlay" href={liveOverlayLink} />
                          <LinkRow label="Game Scorecard Overlay" href={scorecardOverlayLink} />
                        </Stack>
                      </Box>
                      <Chip
                        label={status.label}
                        size="small"
                        sx={{
                          bgcolor: status.color,
                          color: 'common.white',
                          fontWeight: 700,
                          ml: 1
                        }}
                      />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            <Stack spacing={1.5}>
              <Typography variant="h6" fontWeight={700}>
                Log Overlay
              </Typography>

              <Paper sx={{ ...cardSx, borderColor: 'warning.light' }}>
                <Stack spacing={0.5}>
                  <Typography fontWeight={600}>Event Log Ticker</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uses the event ticker overlay for live log-style scoring updates.
                  </Typography>
                  <LinkRow label="Event Log Ticker Overlay" href={buildLogOverlayLink()} />
                </Stack>
              </Paper>

              <Paper sx={{ ...cardSx, borderColor: 'secondary.light' }}>
                <Stack spacing={0.5}>
                  <Typography fontWeight={600}>TV Logs Overlay</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uses the TV logs overlay with section cards and automatic page rotation.
                  </Typography>
                  <LinkRow label="TV Logs Overlay" href={buildTvLogsOverlayLink()} />
                </Stack>
              </Paper>

              {eventSections.length > 0 && (
                <Paper sx={{ ...cardSx, borderColor: 'primary.light' }}>
                  <Stack spacing={0.75}>
                    <Typography fontWeight={600}>Section Scorecards Overlays</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Per-section overlays showing round scorecards.
                    </Typography>
                    {eventSections.map((section) => {
                      const resolvedSectionName = section.name || section.sectionName || section.id;
                      const sectionLink = buildSectionScorecardsOverlayLink(
                        section.id,
                        resolvedSectionName
                      );
                      return (
                        <Box key={section.id}>
                          <Typography variant="body2" fontWeight={700}>
                            {resolvedSectionName}
                          </Typography>
                          <LinkRow label="Section Scorecards Overlay" href={sectionLink} />
                        </Box>
                      );
                    })}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

export default OverlayIndex;
